import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import Lottie from 'react-lottie-player';
import OrdersManager from '../../services/api/OrdersManager';
import { OrderFulfillmentStatus, OrderMethod, PaymentMethod } from '../../services/exports/Constants';
import PrinterIcon from '../../resources/assets/icons/PrinterIcon';
import usePrinter from '../../hooks/usePrinter';
import LoadingIndicatorLight from '../../resources/assets/animations/LoadingIndicatorLight.json';
import { useSelector } from 'react-redux';
import { ReceiptType } from '../../providers/PrintProvider';
import { useFooter } from '../../providers/FooterProvider';
import { useOngoingOrders } from '../../providers/OngoingOrdersProvider';
import { useHelpers } from '../../providers/HelpersProvider';
import { useStats } from '../../providers/StatsProvider';
import { Table } from 'antd';
import DateFilter from '../../components/lib/DateFilter';
import Layout from '../../components/layout/Layout';

export default function OrderHistoryView() {
  const { t } = useTranslation(null, { keyPrefix: 'Views:OrderHistory' });

  const { loadStats } = useStats();
  const { setFooterContent } = useFooter();
  const { print, printing } = usePrinter();
  const { toggleOrderDetailsModal } = useOngoingOrders();
  const { formatCurrency, formatDateTime } = useHelpers();

  const { company } = useSelector((state) => state.company);
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({
    orders_count: 0,
    tips: 0,
    sales: 0,
    cash_sales: 0,
    online_sales: 0,
    refunds_count: 0,
    refunds_amount: 0,
    online_collected_taxes_list: [],
    cash_collected_taxes_list: [],
  });

  const [selectedFilter, setSelectedFilter] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFooterContent(
      <div className="py-md flex justify-center">
        <button
          onClick={() =>
            print(
              {
                type: ReceiptType.OrderHistory,
                payload: {
                  selectedFilter,
                  stats,
                },
              },
              ReceiptType.OrderHistory,
            )
          }
          disabled={printing}
          className="flex justify-center items-center rounded-full px-4 py-2 border-t border-gray-200 bg-black z-50 cursor-pointer"
        >
          {printing ? (
            <Lottie
              play
              loop
              animationData={LoadingIndicatorLight}
              rendererSettings={{
                preserveAspectRatio: 'xMidYMid slice',
              }}
              style={{
                width: '40px',
              }}
            />
          ) : (
            <div className="flex justify-center text-white">
              <PrinterIcon color="white" className="w-6 h-6 mr-2" /> {t('buttons.print')}
            </div>
          )}
        </button>
      </div>,
    );

    return () => setFooterContent(null);
  }, [selectedFilter, stats]);

  useEffect(() => {
    selectedFilter && refreshStats();
    selectedFilter && requestPastOrders();
  }, [selectedFilter]);

  async function refreshStats() {
    const { success, data } = await loadStats(
      selectedFilter.start_date,
      selectedFilter.end_date,
      [
        'orders_count',
        'cash_orders_count',
        'online_orders_count',
        'refunds_count',

        'tips',
        'sales',
        'cash_sales',
        'online_sales',
        'refunds_amount',

        'delivery_fees',
        'cash_delivery_fees',
        'online_delivery_fees',

        'online_collected_taxes_list',
        'cash_collected_taxes_list',
      ],
      false,
    );

    if (success) {
      setStats(data);
    }
  }

  async function requestPastOrders(page = 1) {
    setLoading(true);
    const { data, success } = await OrdersManager.get({
      page,
      sort: '-order_fulfillments.created_at',
      filter: {
        takeout: true,
        'fulfillment.created_between':
          selectedFilter.start_date && selectedFilter.end_date
            ? `${selectedFilter.start_date},${selectedFilter.end_date}`
            : null,
      },
    });
    setLoading(false);

    if (success) {
      return setData(data);
    }

    return toast.error(t('toasts.failed_to_fetch_orders'));
  }

  return (
    <Layout.Page>
      <div>
        <DateFilter onChange={setSelectedFilter} />
        <div className="mt-md space-y-xs">
          <p className="mt-md">{t('labels.orders_number', { count: stats.orders_count })}</p>
          <p>
            {t('labels.revenue', { amount: formatCurrency(stats.sales) })}
            {stats.cash_sales > 0 && t('labels.cash_sales', { amount: formatCurrency(stats.cash_sales) })}
          </p>
          <p>{t('labels.tips', { amount: formatCurrency(stats.tips) })}</p>
          <p className="text-danger mb-0">
            {t('labels.refunds', {
              count: stats.refunds_count,
              amount: formatCurrency(stats.refunds_amount),
            })}
          </p>
        </div>
      </div>
      <div className="mt-md">
        <Table
          className="bg-white overflow-x-auto"
          dataSource={data.data}
          loading={loading}
          rowKey={(item) => item.id}
          onRow={(item) => ({
            onClick: () => toggleOrderDetailsModal(item),
          })}
          pagination={{
            current: data?.meta?.current_page,
            total: data?.meta?.total,
            pageSize: data?.meta?.per_page,
            onChange: (page) => requestPastOrders(page),
          }}
          rowClassName={(item) =>
            classnames('cursor-pointer', {
              'text-danger': item.fulfillment_status === OrderFulfillmentStatus.Rejected,
            })
          }
          columns={[
            {
              key: 'order_placed',
              title: t('fields.order_placed'),
              render: (item) => formatDateTime(item.created_at),
            },
            {
              key: 'order_number',
              title: t('fields.order_number'),
              render: (item) => (
                <span className={classnames({ 'text-danger': item.is_dummy })}>
                  {item.is_dummy ? t('labels.dummy_order') : `#${item.number}`}
                </span>
              ),
            },
            {
              key: 'customer',
              title: t('fields.customer'),
              render: (item) => `${item.customer_first_name} ${item.customer_last_name}`,
            },
            {
              key: 'payment_method',
              title: t('fields.payment_method'),
              render: (item) =>
                item.payment.payment_method_id === PaymentMethod.Cash
                  ? t(company?.overwrite_cash_payments_to_invoice ? 'payment_methods.invoice' : 'payment_methods.cash')
                  : t('payment_methods.online'),
            },
            {
              key: 'method',
              title: t('fields.method'),
              render: (item) =>
                t(
                  item.method === OrderMethod.OnSite
                    ? `order_methods.${item.method}.${item?.room_service_location?.type}`
                    : `order_methods.${item.method}`,
                ),
            },
            {
              key: 'status',
              title: t('fields.status'),
              render: (item) => t(`statuses.${item.fulfillment_status}`),
            },
            {
              key: 'total',
              title: t('fields.total'),
              render: (item) => formatCurrency(item.total_amount),
            },
          ]}
        />
      </div>
    </Layout.Page>
  );
}
